import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, MapPin, Flag, Share2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAuthGate } from "@/components/AuthGate";
import { Button } from "@/components/ui/button";
import { ReportDialog, ShareDialog } from "@/components/dialogs";
import { Rating, PageLoading, VerifBadge } from "@/components/common";

export default function ServiceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const gate = useAuthGate();
  const [data, setData] = useState(null);
  const [report, setReport] = useState(false);
  const [share, setShare] = useState(false);
  useEffect(() => { api.get(`/services/${id}`).then(({ data }) => setData(data)); }, [id]);
  if (!data) return <PageLoading />;
  const s = data.service;
  const message = async () => {
    if (!gate.require("Create a free account to message this service provider.")) return;
    if (s.owner_id === user.id) return toast.info("This is your service.");
    try { const { data } = await api.post("/conversations", { recipient_id: s.owner_id, context_type: "service", context_id: id, context_label: s.name, message: `Hi, I'm interested in your ${s.category} service.` }); nav(`/messages/${data.conversation_id}`); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div className="rounded-2xl border border-border bg-card p-5">
        <p className="text-sm font-semibold text-primary">{s.category}</p>
        <h1 className="font-display text-2xl font-bold">{s.name}</h1>
        <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" />{s.suburb ? `${s.suburb}, ` : ""}{s.state}</p>
        <div className="mt-2 flex items-center gap-2"><Rating value={s.rating} count={s.review_count} />{s.verified && <VerifBadge type="verified" />}</div>
        {s.description && <p className="mt-3 text-sm leading-relaxed text-foreground/80">{s.description}</p>}
        {s.price_info && <p className="mt-2 text-sm"><b>Pricing:</b> {s.price_info}</p>}
        <div className="mt-4 flex gap-2">
          <Button onClick={message} data-testid="svc-message"><MessageSquare className="h-4 w-4" />Contact provider</Button>
          <Button variant="outline" onClick={() => setShare(true)} data-testid="svc-share"><Share2 className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={() => user ? setReport(true) : gate.require("Create a free account to report services.")} data-testid="svc-report"><Flag className="h-4 w-4" /></Button>
        </div>
      </div>
      <ReportDialog open={report} onOpenChange={setReport} targetType="service" targetId={id} reportedUserId={s.owner_id} reportedUserName={s.name} onBlocked={() => nav("/explore")} />
      <ShareDialog open={share} onOpenChange={setShare} url={`/service/${id}`} title={s.name} />
    </div>
  );
}
